import actionCreatorFactory from "typescript-fsa";
import { reducerWithInitialState } from "typescript-fsa-reducers";
import {
  Assign,
  AssignGroupingKey,
  DefaultGroupingExpansionState,
  JobForm,
  KeyValue,
} from "../../types/types";
import { nenki } from "../../util/util";

export interface GridSortState {
  property: string;
  ascending: string;
}

export interface GridGroupState {
  propertyName: string;
}

export interface GridState {
  columns: string;
  filters: string;
  sortings: GridSortState[];
  groupings: GridGroupState[];
}

export interface AssignFilterItem {
  id: string;
  label: string;
  icon: string;
  defaultGroupingKey: AssignGroupingKey;
  defaultGroupingExpansionState: DefaultGroupingExpansionState;
  customFilterCondition?: KeyValue[];
  itemCount?: number;
}

export interface AssignChanges {
  itemsAdded: Assign[];
  itemsEdited: Assign[];
  itemsRemoved: Assign[];
}

export interface AssignPageState {
  nenki: string;
  diff?: any;
  gridState?: GridState;
  selectedAssignId?: number;
  assignOrder?: number[];
  defaultFilters: AssignFilterItem[];
  customFilters: AssignFilterItem[];
  selectedFilterId: string;
  textSearchConditions: KeyValue[];
  selectedGroupingCondition: AssignGroupingKey;
  defaultGroupingExpansionState: DefaultGroupingExpansionState;
}

const defaultFilterItems: AssignFilterItem[] = [
  {
    id: "all",
    label: "全件表示",
    icon: "AllApps",
    defaultGroupingKey: "jobCode",
    defaultGroupingExpansionState: "neutral",
  },
  {
    id: "unsaved_changes",
    label: "未保存",
    icon: "EditMirrored",
    defaultGroupingKey: "jobCode",
    defaultGroupingExpansionState: "neutral",
  },
  {
    id: "filled_status_by_employee",
    label: "人別充足状況",
    icon: "Contact",
    defaultGroupingKey: "inChargeKojinCode",
    defaultGroupingExpansionState: "neutral",
  },
  {
    id: "unfilled_employee",
    label: "未充足(担当)",
    icon: "Warning",
    defaultGroupingKey: "inChargeKojinCode",
    defaultGroupingExpansionState: "neutral",
  },
  {
    id: "unfilled_job",
    label: "未充足(ジョブ)",
    icon: "Warning", // AlertSolid Error IncidentTriangle Warning Warning12
    defaultGroupingKey: "jobCode",
    defaultGroupingExpansionState: "neutral",
  },
];

export const assignPageInitialState: AssignPageState = {
  nenki: nenki.current,
  defaultFilters: defaultFilterItems,
  customFilters: [],
  selectedFilterId: "all",
  textSearchConditions: [],
  selectedGroupingCondition: "jobCode",
  defaultGroupingExpansionState: "neutral",
};

const actionCreator = actionCreatorFactory("assign-page");
export const assignPageActions = {
  /* SYSTEM ACTIONS */
  SYSTEM_INITIALIZE_APPLICATION: actionCreator("SYSTEM_INITIALIZE_APPLICATION"),
  SYSTEM_UPDATE_ASSIGN_ORDER: actionCreator<{ assignOrder: number[] }>(
    "SYSTEM_UPDATE_ASSIGN_ORDER"
  ),
  SYSTEM_DELETE_SELECTED_JOB: actionCreator("SYSTEM_DELETE_SELECTED_JOB"),
  SYSTEM_DELETE_CUSTOM_FILTER: actionCreator<{
    filterId: string;
  }>("SYSTEM_DELETE_CUSTOM_FILTER"),

  /* USER ACTIONS */
  USER_CHNAGE_NENKI: actionCreator<{ nenki: string }>("USER_CHNAGE_NENKI"),
  USER_INSERT_ASSIGN: actionCreator("USER_INSERT_ASSIGN"),
  USER_EDIT_ASSIGN: actionCreator<{ assigns: Assign[] }>("USER_EDIT_ASSIGN"),
  USER_DELETE_ASSIGN: actionCreator("USER_DELETE_ASSIGN"),
  USER_SAVE_ASSIGNS: actionCreator("USER_SAVE_ASSIGNS"),
  USER_CLICKED_ASSIGN_SAVE_BUTTON: actionCreator(
    "USER_CLICKED_ASSIGN_SAVE_BUTTON"
  ),
  USER_RELOAD_ASSIGNS_LIST: actionCreator<{ message?: string }>(
    "USER_RELOAD_ASSIGNS_LIST"
  ),
  USER_RESET_ASSIGNS_LIST: actionCreator("USER_RESET_ASSIGNS_LIST"),
  USER_CHANGE_GRID_STATE: actionCreator<{ gridState: GridState }>(
    "USER_CHANGE_GRID_STATE"
  ),
  USER_SELECT_ASSIGN: actionCreator<{
    id?: number;
    rowIndex?: number;
  }>("USER_SELECT_ASSIGN"),
  USER_CHANGE_ASSIGN_FILTER: actionCreator<{ selectedFilterId: string }>(
    "USER_CHANGE_ASSIGN_FILTER"
  ),
  USER_CLICKED_JOB_CREATE_BUTTON: actionCreator(
    "USER_CLICKED_JOB_CREATE_BUTTON"
  ),
  USER_CLICKED_JOB_UPDATE_BUTTON: actionCreator(
    "USER_CLICKED_JOB_UPDATE_BUTTON"
  ),
  USER_CLICKED_JOB_DELETE_BUTTON: actionCreator(
    "USER_CLICKED_JOB_DELETE_BUTTON"
  ),
  USER_CLICKED_JOB_REGISTER_BUTTON: actionCreator<{ jobForm: JobForm }>(
    "USER_CLICKED_JOB_REGISTER_BUTTON"
  ),
  USER_CHANGE_TEXT_SEARCH_CONDITION: actionCreator<KeyValue[]>(
    "USER_CHANGE_TEXT_SEARCH_CONDITION"
  ),
  USER_CLICKED_CUSTOM_FILTER_REGISTER_BUTTON: actionCreator(
    "USER_CLICKED_CUSTOM_FILTER_REGISTER_BUTTON"
  ),
  USER_CLICKED_SUBMIT_CUSTOM_FILTER_BUTTON: actionCreator<{
    filterName: string;
    defaultGroupingKey: AssignGroupingKey;
    defaultGroupingExpansionState: DefaultGroupingExpansionState;
  }>("USER_CLICKED_SUBMIT_CUSTOM_FILTER_BUTTON"),
  USER_SELECT_GROUPING_CONDITION: actionCreator<{
    groupingKey: AssignGroupingKey;
  }>("USER_SELECT_GROUPING_CONDITION"),
  USER_CLICKED_FILTER_MANAGEMENT_BUTTON: actionCreator(
    "USER_CLICKED_FILTER_MANAGEMENT_BUTTON"
  ),
  USER_REORDER_CUSTOM_FILTERS: actionCreator<{ filters: AssignFilterItem[] }>(
    "USER_REORDER_CUSTOM_FILTERS"
  ),
  USER_CLICKED_SAVE_CUSTOM_FILTER_BUTTON: actionCreator<{
    filters: AssignFilterItem[];
  }>("USER_CLICKED_SAVE_CUSTOM_FILTER_BUTTON"),
  USER_CLICKED_DELETE_CUSTOM_FILTER_BUTTON: actionCreator<{
    filterId: string;
  }>("USER_CLICKED_DELETE_CUSTOM_FILTER_BUTTON"),
  USER_CLICKED_OPEN_CHANGE_HISTORY_BUTTON: actionCreator(
    "USER_CLICKED_OPEN_CHANGE_HISTORY_BUTTON"
  ),

  /* REDUCER ACTIONS */
  REDUCER_SET_ASSIGN_PAGE_ROOT_STATE: actionCreator<{ state: AssignPageState }>(
    "REDUCER_SET_ASSIGN_PAGE_ROOT_STATE"
  ),
  REDUCER_SET_NENKI: actionCreator<{
    nenki: string;
  }>("REDUCER_SET_NENKI"),
  REDUCER_SET_GRID_STATE: actionCreator<{
    gridState?: GridState;
  }>("REDUCER_SET_GRID_STATE"),
  REDUCER_SET_SELECTED_ASSIGN_ID: actionCreator<{ id?: number }>(
    "REDUCER_SET_SELECTED_ASSIGN_ID"
  ),
  REDUCER_SET_ASSIGN_ORDER: actionCreator<{ assignOrder: number[] }>(
    "REDUCER_SET_ASSIGN_ORDER"
  ),
  REDUCER_CALC_DIFF_DONE: actionCreator<{ diff: any }>(
    "REDUCER_CALC_DIFF_DONE"
  ),
  REDUCER_SET_ASSIGN_FILTER: actionCreator<{ selectedFilterId: string }>(
    "REDUCER_SET_ASSIGN_FILTER"
  ),
  REDUCER_SET_TEXT_SEARCH_CONDITIONS: actionCreator<KeyValue[]>(
    "REDUCER_SET_TEXT_SEARCH_CONDITIONS"
  ),
  RECUCER_SET_CUSTOM_FILTERS: actionCreator<AssignFilterItem[]>(
    "RECUCER_SET_CUSTOM_FILTERS"
  ),
  REDUCER_SET_SELECTED_GROUPING_CONDITION: actionCreator<{
    groupingKey: AssignGroupingKey;
  }>("REDUCER_SET_SELECTED_GROUPING_CONDITION"),
  REDUCER_SET_DEFAULT_GROUPING_EXPANSION_STATE: actionCreator<{
    defaultGroupingExpansionState: DefaultGroupingExpansionState;
  }>("REDUCER_SET_DEFAULT_GROUPING_EXPANSION_STATE"),

  // OLD
  USER_SHOW_DIFF_REQUESTED: actionCreator("USER_SHOW_DIFF_REQUESTED"),
};

/**
 * アサイン画面リデューサー
 */
export const assignPageReducer = reducerWithInitialState<AssignPageState>(
  assignPageInitialState
)
  .case(
    assignPageActions.REDUCER_SET_ASSIGN_PAGE_ROOT_STATE,
    (state, payload) => payload.state
  )
  .case(assignPageActions.REDUCER_SET_NENKI, (state, payload) => ({
    ...state,
    nenki: payload.nenki,
  }))
  .case(assignPageActions.REDUCER_SET_GRID_STATE, (state, payload) => ({
    ...state,
    gridState: payload.gridState,
  }))
  .case(assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID, (state, payload) => ({
    ...state,
    selectedAssignId: payload.id,
  }))
  .case(assignPageActions.REDUCER_SET_ASSIGN_ORDER, (state, payload) => ({
    ...state,
    assignOrder: payload.assignOrder,
  }))
  .case(assignPageActions.REDUCER_SET_ASSIGN_FILTER, (state, payload) => ({
    ...state,
    selectedFilterId: payload.selectedFilterId,
  }))
  .case(
    assignPageActions.REDUCER_SET_TEXT_SEARCH_CONDITIONS,
    (state, payload) => ({
      ...state,
      textSearchConditions: payload,
    })
  )
  .case(assignPageActions.RECUCER_SET_CUSTOM_FILTERS, (state, payload) => ({
    ...state,
    customFilters: payload,
  }))
  .case(
    assignPageActions.REDUCER_SET_SELECTED_GROUPING_CONDITION,
    (state, payload) => ({
      ...state,
      selectedGroupingCondition: payload.groupingKey,
    })
  )
  .case(
    assignPageActions.REDUCER_SET_DEFAULT_GROUPING_EXPANSION_STATE,
    (state, payload) => ({
      ...state,
      defaultGroupingExpansionState: payload.defaultGroupingExpansionState,
    })
  );
